// src/hooks/useActiveAddons.js
//
// Returns the set of active add-on SKUs for the calling practice.
//
// RLS on practice_addons scopes the read to the caller's practice
// (policy: "practice_addons staff read own" - is_staff() AND practice_id =
// my_practice_id()), so this is just a plain SELECT - no practice_id filter
// needed. Subscription_addons is a public catalog readable by all.
//
// PostgREST embed: the FK practice_addons.addon_id -> subscription_addons.id
// exists (verified May 2026) so the embed works. Two-step fallback is not
// needed.
//
// Used by CareManagementView for AMH Care Management Bundle gating. Will
// eventually power Pro feature gating, AI Scribe access checks, and any
// other tier+addon combinations as the addon catalog grows.
//
// API (unchanged):
//   const { skus, has, loading, error, refetch } = useActiveAddons();
//   if (has("care_management_bundle")) { ... }
//
// Server-state lives in TanStack Query (see main.jsx QueryClientProvider).
// The fetch itself is in lib/addonsApi.js; this file owns the alias logic
// and the React shape. Return contract is identical to the hand-rolled
// version it replaced, so no consumer changes.

import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthProvider";
import { fetchActiveAddonSkus } from "../lib/addonsApi";

// Stable identity for "no add-ons" so `has` stays referentially stable
// across renders that resolve to an empty set (defined above the alias block
// so the contract test's source slice stays pure).
const EMPTY_SKUS = new Set();

// SKU ALIASES - equivalence classes for SKUs being renamed.
//
// A SKU rename cannot be atomic here: the database migration and the Vercel
// deploy are separate events, and whichever lands first opens a window where
// every gate keyed on the old string returns false. For the CM bundle that
// means the entire Care Management console disappears for a live practice.
//
// So renames go expand -> migrate -> contract:
//   1. (this) teach the client that both strings mean the same entitlement
//   2. rename the row in subscription_addons - safe in either deploy order
//   3. move the call sites to the new string, then delete the alias
//
// Each inner array is one entitlement. Order within an array does not matter.
const SKU_ALIASES = [
  // "AMH Care Management Bundle" is being renamed because the capability serves
  // every NC Medicaid care management entity - AMH Tier 3, AMH+, CMA, CIN - not
  // just AMH practices. A CMA reading an "AMH" product name reasonably concludes
  // it is not for them.
  // First entry is the current name (canonicalSku returns it). Call sites
  // use it; the old name stays here until no cached client sends it.
  ["care_management_bundle", "amh_care_management_bundle"],
];

const ALIAS_INDEX = (() => {
  const m = new Map();
  for (const group of SKU_ALIASES) {
    for (const sku of group) m.set(sku, group);
  }
  return m;
})();

// True when the owned set contains the requested SKU or any of its aliases.
// Exported so callers that hold a raw Set (AddonRoute) resolve identically to
// callers using the hook - a second, subtly different implementation is how
// half a rename ships.
export function skuMatches(ownedSkus, sku) {
  if (!ownedSkus) return false;
  const group = ALIAS_INDEX.get(sku);
  if (!group) return ownedSkus.has(sku);
  return group.some((s) => ownedSkus.has(s));
}

// Canonical key for lookups in SKU-keyed maps (copy tables, capability grids)
// during a rename. Returns the first name in the alias group, which is the
// one those maps are still keyed by.
export function canonicalSku(sku) {
  const group = ALIAS_INDEX.get(sku);
  return group ? group[0] : sku;
}

export function useActiveAddons() {
  const { practiceId } = useAuth();

  // Cache key includes the EFFECTIVE practice. useAuth().practiceId already
  // resolves to the spectated practice in super-admin "view as owner" mode,
  // so entering/exiting spectator mode selects a different cache entry and
  // one practice's entitlements are never served from another's cache.
  //
  // The query stays enabled even while practiceId is still null (auth
  // resolving): that matches the original hook, which fetched
  // unconditionally on mount, and keeps `loading` true until real data
  // lands - so route gates (AddonRoute) never see "loaded + empty" during
  // the auth window and redirect a user off a bundle route.
  const { data, error: queryError, isFetching, refetch } = useQuery({
    queryKey: ["active-addons", practiceId ?? null],
    queryFn: fetchActiveAddonSkus,
    // Entitlements change rarely. Explicit here so a future change to the
    // provider default cannot silently alter gating latency.
    staleTime: 60 * 1000,
  });

  // On error return an EMPTY set, never stale data - preserves the original
  // "under-show a tab rather than show a bundle the practice doesn't own"
  // rule. (React Query would otherwise keep the last good data on a failed
  // refetch, which is exactly the optimism the original refused.)
  const skus = queryError ? EMPTY_SKUS : (data ?? EMPTY_SKUS);

  // Convenience predicate: useActiveAddons().has("care_management_bundle")
  // Stable across renders so callers can use it in dependency arrays without
  // triggering re-runs.
  const has = useCallback((sku) => skuMatches(skus, sku), [skus]);

  return {
    skus,
    has,
    // isFetching is true on the first load AND during an explicit refetch(),
    // matching the original `loading` (which flipped true on every load()).
    // A cache hit inside staleTime is the one new case: false immediately.
    loading: isFetching,
    // Original exposed a string; keep that contract.
    error: queryError ? (queryError.message || "Failed to load active addons") : null,
    refetch,
  };
}
